<template>
  <CurrentEventDataProvider v-slot="{ hasFullEventDetails }">
    <div
      class="profile-page"
      style="
        background-color: var(--background-color, #f3f4f6);
        min-height: 100vh;
        font-family: 'Poppins', sans-serif;
      "
    >
      <Navbar :show-nav="true" />

      <div
        v-if="hasFullEventDetails"
        class="profile-wrap"
        id="main-content"
        role="main"
      >
        <!-- ── Header ─────────────────────────────────────────────── -->
        <div class="profile-header">
          <h1 class="profile-header__title">
            {{
              isOwnProfile
                ? $t('Components.shared.Navbar.myProfile')
                : displayedFullName || $t('Views.ProfilePage.profile')
            }}
          </h1>
        </div>

        <!-- ── Tab bar (own profile) — splits the page into Profile info,
             Matching profile and My programs. ───────────────────────── -->
        <nav
          v-if="isOwnProfile && profileTabs.length > 1"
          class="profile-tabs"
          role="tablist"
          :aria-label="$t('Views.ProfilePage.profileSections')"
        >
          <button
            v-for="tab in profileTabs"
            :key="tab.key"
            type="button"
            class="profile-tab"
            :class="{ 'profile-tab--active': activeTab === tab.key }"
            role="tab"
            :aria-selected="activeTab === tab.key ? 'true' : 'false'"
            @click="activeTab = tab.key"
          >
            <i :class="tab.icon" aria-hidden="true"></i>
            <span>{{ tab.label }}</span>
          </button>
        </nav>

        <!-- ══ TAB: Profile info ═══════════════════════════════════════ -->
        <div
          v-show="activeTab === 'info'"
          class="profile-tab-panel"
          role="tabpanel"
        >
          <!-- ── Profile Card ─────────────────────────────────────── -->
          <div class="profile-card">
            <div class="profile-card__top">
              <!-- Avatar — own profile gets a camera button to change /
                 take / remove the photo. -->
              <div class="avatar-wrap">
                <Avatar
                  :image="avatarImage"
                  :circle="true"
                  :name="displayedFullName"
                  :alt="displayedFullName"
                  class="avatar-wrap__img"
                />
                <button
                  v-if="isOwnProfile"
                  type="button"
                  class="avatar-wrap__edit"
                  :aria-label="$t('Views.ProfilePage.changeProfilePhoto')"
                  :aria-expanded="avatarMenuOpen ? 'true' : 'false'"
                  @click.stop="avatarMenuOpen = !avatarMenuOpen"
                >
                  <i class="fas fa-camera" aria-hidden="true"></i>
                </button>
                <div v-else class="avatar-wrap__badge" aria-hidden="true">
                  <i class="fas fa-award"></i>
                </div>

                <!-- Photo options menu -->
                <div
                  v-if="avatarMenuOpen && isOwnProfile"
                  class="avatar-menu"
                  @click.stop
                >
                  <button
                    type="button"
                    class="avatar-menu__item"
                    @click="triggerAvatarUpload"
                  >
                    <i class="fas fa-upload"></i>
                    {{ $t('Views.ProfilePage.uploadFromComputer') }}
                  </button>
                  <button
                    type="button"
                    class="avatar-menu__item"
                    @click="openCamera"
                  >
                    <i class="fas fa-camera"></i>
                    {{ $t('Views.ProfilePage.takePhoto') }}
                  </button>
                  <button
                    v-if="displayedUser.Picture"
                    type="button"
                    class="avatar-menu__item avatar-menu__item--danger"
                    @click="onRemoveAvatar"
                  >
                    <i class="far fa-trash-alt"></i>
                    {{ $t('Views.ProfilePage.removePhoto') }}
                  </button>
                </div>
              </div>
              <input
                ref="avatarInput"
                type="file"
                accept="image/*"
                class="avatar-hidden-file"
                @change="onAvatarFileSelected"
              />

              <!-- Name + tags -->
              <div class="profile-card__name-block">
                <h2 class="profile-card__name">
                  <span v-if="inlineEdit.key === 'name'" class="field-inline">
                    <input
                      ref="inlineInput"
                      v-model="inlineEdit.first"
                      class="field-input"
                      :placeholder="$t('Views.Stage.MyProfile.firstName')"
                      @keyup.enter="saveInlineEdit"
                      @keyup.esc="cancelInlineEdit"
                    />
                    <input
                      v-model="inlineEdit.last"
                      class="field-input"
                      :placeholder="$t('Views.Stage.MyProfile.lastName')"
                      @keyup.enter="saveInlineEdit"
                      @keyup.esc="cancelInlineEdit"
                    />
                    <button
                      type="button"
                      class="field-save"
                      :disabled="inlineEdit.saving"
                      :aria-label="$t('Views.Stage.MyProfile.save')"
                      @click="saveInlineEdit"
                    >
                      <i class="fas fa-check"></i>
                    </button>
                    <button
                      type="button"
                      class="field-cancel"
                      :aria-label="$t('Views.ProfilePage.cancel')"
                      @click="cancelInlineEdit"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </span>
                  <template v-else>
                    {{ displayedUser.Firstname }} {{ displayedUser.Lastname }}
                    <button
                      v-if="isOwnProfile"
                      type="button"
                      class="field-edit"
                      :aria-label="$t('Views.ProfilePage.editName')"
                      :title="$t('Views.ProfilePage.edit')"
                      @click="startInlineEdit('name')"
                    >
                      <i class="fas fa-pen" aria-hidden="true"></i>
                    </button>
                  </template>
                </h2>
                <div class="profile-card__tags">
                  <span class="tag tag--filled">{{
                    displayedUser.TicketType || $t('Views.ProfilePage.attendee')
                  }}</span>
                  <span class="tag tag--outline">{{
                    $t('Views.ProfilePage.registered')
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Contact info grid -->
            <div class="contact-grid">
              <div
                class="contact-item"
                v-if="
                  isOwnProfile
                    ? displayedUser.Email
                    : participantEmailVisible(displayedUser)
                "
              >
                <div class="contact-item__icon contact-item__icon--teal">
                  <i class="far fa-envelope" aria-hidden="true"></i>
                </div>
                <template v-if="inlineEdit.key === 'Email'">
                  <input
                    ref="inlineInput"
                    v-model="inlineEdit.value"
                    type="email"
                    class="field-input"
                    @keyup.enter="saveInlineEdit"
                    @keyup.esc="cancelInlineEdit"
                  />
                  <button
                    type="button"
                    class="field-save"
                    :disabled="inlineEdit.saving"
                    :aria-label="$t('Views.Stage.MyProfile.save')"
                    @click="saveInlineEdit"
                  >
                    <i class="fas fa-check"></i>
                  </button>
                  <button
                    type="button"
                    class="field-cancel"
                    :aria-label="$t('Views.ProfilePage.cancel')"
                    @click="cancelInlineEdit"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </template>
                <template v-else>
                  <span class="contact-item__text">{{
                    displayedUser.Email
                  }}</span>
                  <button
                    v-if="isOwnProfile"
                    type="button"
                    class="field-edit"
                    :aria-label="$t('Views.ProfilePage.editEmail')"
                    :title="$t('Views.ProfilePage.edit')"
                    @click="startInlineEdit('Email')"
                  >
                    <i class="fas fa-pen" aria-hidden="true"></i>
                  </button>
                </template>
              </div>
              <div class="contact-item" v-if="displayedUser.Jobtitle">
                <div class="contact-item__icon contact-item__icon--purple">
                  <i class="fas fa-briefcase" aria-hidden="true"></i>
                </div>
                <template v-if="inlineEdit.key === 'Jobtitle'">
                  <input
                    ref="inlineInput"
                    v-model="inlineEdit.value"
                    class="field-input"
                    @keyup.enter="saveInlineEdit"
                    @keyup.esc="cancelInlineEdit"
                  />
                  <button
                    type="button"
                    class="field-save"
                    :disabled="inlineEdit.saving"
                    :aria-label="$t('Views.Stage.MyProfile.save')"
                    @click="saveInlineEdit"
                  >
                    <i class="fas fa-check"></i>
                  </button>
                  <button
                    type="button"
                    class="field-cancel"
                    :aria-label="$t('Views.ProfilePage.cancel')"
                    @click="cancelInlineEdit"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </template>
                <template v-else>
                  <span class="contact-item__text">{{
                    displayedUser.Jobtitle
                  }}</span>
                  <button
                    v-if="isOwnProfile"
                    type="button"
                    class="field-edit"
                    :aria-label="$t('Views.ProfilePage.editJobTitle')"
                    :title="$t('Views.ProfilePage.edit')"
                    @click="startInlineEdit('Jobtitle')"
                  >
                    <i class="fas fa-pen" aria-hidden="true"></i>
                  </button>
                </template>
              </div>
              <div class="contact-item" v-if="displayedUser.Company">
                <div class="contact-item__icon contact-item__icon--orange">
                  <i class="far fa-building" aria-hidden="true"></i>
                </div>
                <template v-if="inlineEdit.key === 'Company'">
                  <input
                    ref="inlineInput"
                    v-model="inlineEdit.value"
                    class="field-input"
                    @keyup.enter="saveInlineEdit"
                    @keyup.esc="cancelInlineEdit"
                  />
                  <button
                    type="button"
                    class="field-save"
                    :disabled="inlineEdit.saving"
                    :aria-label="$t('Views.Stage.MyProfile.save')"
                    @click="saveInlineEdit"
                  >
                    <i class="fas fa-check"></i>
                  </button>
                  <button
                    type="button"
                    class="field-cancel"
                    :aria-label="$t('Views.ProfilePage.cancel')"
                    @click="cancelInlineEdit"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </template>
                <template v-else>
                  <span class="contact-item__text">{{
                    displayedUser.Company
                  }}</span>
                  <button
                    v-if="isOwnProfile"
                    type="button"
                    class="field-edit"
                    :aria-label="$t('Views.ProfilePage.editCompany')"
                    :title="$t('Views.ProfilePage.edit')"
                    @click="startInlineEdit('Company')"
                  >
                    <i class="fas fa-pen" aria-hidden="true"></i>
                  </button>
                </template>
              </div>
              <div
                class="contact-item"
                v-if="
                  isOwnProfile
                    ? displayedPhone
                    : participantPhoneVisible(displayedUser)
                "
              >
                <div class="contact-item__icon contact-item__icon--green">
                  <i class="fas fa-phone" aria-hidden="true"></i>
                </div>
                <template v-if="inlineEdit.key === 'Phone'">
                  <input
                    ref="inlineInput"
                    v-model="inlineEdit.value"
                    type="tel"
                    class="field-input"
                    @keyup.enter="saveInlineEdit"
                    @keyup.esc="cancelInlineEdit"
                  />
                  <button
                    type="button"
                    class="field-save"
                    :disabled="inlineEdit.saving"
                    :aria-label="$t('Views.Stage.MyProfile.save')"
                    @click="saveInlineEdit"
                  >
                    <i class="fas fa-check"></i>
                  </button>
                  <button
                    type="button"
                    class="field-cancel"
                    :aria-label="$t('Views.ProfilePage.cancel')"
                    @click="cancelInlineEdit"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </template>
                <template v-else>
                  <span class="contact-item__text">{{ displayedPhone }}</span>
                  <button
                    v-if="isOwnProfile"
                    type="button"
                    class="field-edit"
                    :aria-label="$t('Views.ProfilePage.editPhone')"
                    :title="$t('Views.ProfilePage.edit')"
                    @click="startInlineEdit('Phone')"
                  >
                    <i class="fas fa-pen" aria-hidden="true"></i>
                  </button>
                </template>
              </div>
            </div>

            <!-- Participation toggle — own profile only, and only on hybrid
               events: elsewhere the event setup decides the platform. -->
            <fieldset
              v-if="isOwnProfile && isHybridEvent"
              class="participate-box"
            >
              <legend class="participate-box__label">
                {{ $t('Views.ProfilePage.howDoYouParticipate') }}
              </legend>
              <div class="participate-box__options">
                <label class="radio-option">
                  <input
                    type="radio"
                    name="participationType"
                    value="physical"
                    v-model="participationType"
                  />
                  <span class="radio-option__dot"></span>
                  <span class="radio-option__label">{{
                    $t('Views.ProfilePage.physical')
                  }}</span>
                </label>
                <label class="radio-option">
                  <input
                    type="radio"
                    name="participationType"
                    value="digital"
                    v-model="participationType"
                  />
                  <span class="radio-option__dot"></span>
                  <span class="radio-option__label">{{
                    $t('Views.ProfilePage.digital')
                  }}</span>
                </label>
              </div>
            </fieldset>

            <!-- Profile visibility — own profile only. Lets the user
               choose whether they appear in the participant list and
               whether their email/phone are shown to others. Only
               relevant when the admin has the participant list enabled. -->
            <fieldset
              v-if="isOwnProfile && isVisibilityControlShown"
              class="visibility-box"
            >
              <legend class="visibility-box__label">
                {{ $t('Views.ProfilePage.profileVisibility') }}
              </legend>

              <div class="visibility-row">
                <div class="visibility-row__text">
                  <span class="visibility-row__title">{{
                    $t('Views.ProfilePage.showMeInList')
                  }}</span>
                  <span class="visibility-row__desc">{{
                    $t('Views.ProfilePage.showMeInListDesc')
                  }}</span>
                </div>
                <button
                  type="button"
                  class="vis-toggle"
                  :class="{ 'vis-toggle--on': isProfileVisible }"
                  role="switch"
                  :aria-checked="isProfileVisible ? 'true' : 'false'"
                  :aria-label="$t('Views.ProfilePage.showMeInListAria')"
                  @click="onToggleProfileVisible"
                >
                  <span class="vis-toggle__track"
                    ><span class="vis-toggle__thumb"></span
                  ></span>
                </button>
              </div>

              <div class="visibility-row">
                <div class="visibility-row__text">
                  <span class="visibility-row__title">{{
                    $t('Views.ProfilePage.showContact')
                  }}</span>
                  <span class="visibility-row__desc">{{
                    $t('Views.ProfilePage.showContactDesc')
                  }}</span>
                </div>
                <button
                  type="button"
                  class="vis-toggle"
                  :class="{ 'vis-toggle--on': isContactVisible }"
                  role="switch"
                  :aria-checked="isContactVisible ? 'true' : 'false'"
                  :disabled="!isProfileVisible"
                  :aria-label="$t('Views.ProfilePage.showContactAria')"
                  @click="onToggleContactVisible"
                >
                  <span class="vis-toggle__track"
                    ><span class="vis-toggle__thumb"></span
                  ></span>
                </button>
              </div>
            </fieldset>
          </div>
          <!-- end Profile Card -->

          <!-- Table / seat assignment for the participant and their +1s. Own
               profile only: the endpoint answers for the signed-in user, so it
               must never sit on another attendee's page. Renders nothing on
               events without a seating plan. -->
          <SeatingCard v-if="isOwnProfile" />

          <!-- Event Badge + Vouchers — relocated here from the navbar
               menu so they're managed within the Profile info tab. -->
          <div
            v-if="isOwnProfile && (isQrEnabled || isVoucherEnabled)"
            class="profile-actions"
          >
            <button
              v-if="isQrEnabled"
              type="button"
              class="action-tile"
              @click="openEventBadgeModal"
            >
              <span class="action-tile__icon action-tile__icon--badge">
                <i class="fas fa-id-badge" aria-hidden="true"></i>
              </span>
              <span class="action-tile__text">
                <span class="action-tile__title">{{
                  $t('Views.ProfilePage.eventBadge')
                }}</span>
                <span class="action-tile__desc">{{
                  $t('Views.ProfilePage.eventBadgeDesc')
                }}</span>
              </span>
              <i
                class="fas fa-chevron-right action-tile__chev"
                aria-hidden="true"
              ></i>
            </button>
            <button
              v-if="isVoucherEnabled"
              type="button"
              class="action-tile"
              @click="openVoucherModal"
            >
              <span class="action-tile__icon action-tile__icon--voucher">
                <i class="fas fa-gift" aria-hidden="true"></i>
              </span>
              <span class="action-tile__text">
                <span class="action-tile__title">{{
                  $t('Views.ProfilePage.vouchers')
                }}</span>
                <span class="action-tile__desc">{{
                  $t('Views.ProfilePage.vouchersDesc')
                }}</span>
              </span>
              <i
                class="fas fa-chevron-right action-tile__chev"
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
        <!-- end TAB: Profile info -->

        <!-- ══ TAB: Matching profile ═══════════════════════════════════ -->
        <div
          v-if="isOwnProfile && isAiMatchingEnabled"
          v-show="activeTab === 'matching'"
          class="profile-tab-panel profile-networking"
          role="tabpanel"
        >
          <MatchingProfileForm embedded />
        </div>

        <!-- ══ TAB: My Programs ═══════════════════════════════════════
             Own profile only — booked sessions + confirmed meetings +
             registered open sessions. Shown only when the admin's
             "My agenda" (booking) toggle is on. -->
        <div
          v-if="isOwnProfile && isBookingsEnabled"
          v-show="activeTab === 'programs'"
          class="profile-tab-panel"
          role="tabpanel"
        >
          <BookSessionsDataProvider
            v-slot="{ addOrDeleteBooksSesions, isBookSessionLoading }"
          >
            <AgendasDataProvider v-slot="{ trackById, tracks }">
              <div id="my-programs" class="my-programs">
                <!-- Populate the meetings + open-sessions stores so Confirmed
                   meetings and registered open sessions merge into the list
                   below (see BookSessions/mergedMyAgendaGroupedByDay).
                   Renderless; only mounted when AI matching is enabled so we
                   don't hit those endpoints when the feature is off. -->
                <template v-if="isAiMatchingEnabled">
                  <MyMeetingsDataProvider tab="upcoming" v-slot="{}">
                    <span class="hidden"></span>
                  </MyMeetingsDataProvider>
                  <OpenSessionsDataProvider v-slot="{}">
                    <span class="hidden"></span>
                  </OpenSessionsDataProvider>
                </template>

                <div class="my-programs__header">
                  <div class="my-programs__title-row">
                    <h2 class="my-programs__title">
                      {{ $t('Views.ProfilePage.myPrograms') }}
                    </h2>
                    <span class="my-programs__count">{{
                      mergedPrograms.length
                    }}</span>
                  </div>
                  <button
                    class="btn-export"
                    @click="exportPrograms(mergedPrograms, trackById)"
                  >
                    <i class="fas fa-download" aria-hidden="true"></i>
                    {{ $t('Views.ProfilePage.export') }}
                  </button>
                </div>

                <div v-if="!mergedPrograms.length" class="my-programs__empty">
                  <i
                    class="far fa-calendar-times"
                    style="font-size: 32px; color: #d1d5db; margin-bottom: 12px"
                  ></i>
                  <p>
                    {{ $t('Views.ProfilePage.noPrograms') }}
                  </p>
                </div>

                <div v-else class="my-programs__grid">
                  <div
                    v-for="session in mergedPrograms"
                    :key="session.id"
                    class="mp-card"
                    :class="{
                      'mp-card--ended': isPassed(session),
                      'mp-card--clickable': session.kind !== 'agenda'
                    }"
                    :role="session.kind !== 'agenda' ? 'button' : null"
                    :tabindex="session.kind !== 'agenda' ? 0 : null"
                    @click="session.kind !== 'agenda' && openProgram(session)"
                    @keydown.enter="
                      session.kind !== 'agenda' && openProgram(session)
                    "
                  >
                    <!-- Left: time + status -->
                    <div class="mp-card__time-col">
                      <i
                        class="far fa-clock mp-card__clock"
                        aria-hidden="true"
                      ></i>
                      <span class="mp-card__start">{{
                        formatTime(session.startDate)
                      }}</span>
                      <span class="mp-card__end">{{
                        formatTime(session.endDate)
                      }}</span>
                      <span
                        class="mp-card__status"
                        :class="'mp-card__status--' + statusKey(session)"
                      >
                        <span
                          v-if="isLive(session)"
                          class="mp-card__live-dot"
                        ></span>
                        {{ statusLabel(session) }}
                      </span>
                    </div>

                    <!-- Right: info -->
                    <div class="mp-card__body">
                      <h3 class="mp-card__title">
                        {{ session.title }}
                        <span
                          v-if="session.kind === 'meeting'"
                          class="mp-card__kind mp-card__kind--meeting"
                          >{{ $t('Views.Stage.Agenda.kindMeeting') }}</span
                        >
                        <span
                          v-else-if="session.kind === 'openSession'"
                          class="mp-card__kind mp-card__kind--open"
                          >{{ $t('Views.ProfilePage.kindOpenSession') }}</span
                        >
                      </h3>
                      <!-- Agenda sessions: subtitles (meetings show their
                         location in the meta row instead) -->
                      <template v-if="session.kind === 'agenda'">
                        <p v-if="session.subtitle" class="mp-card__subtitle">
                          {{ session.subtitle }}
                        </p>
                        <p v-if="session.subtitle2" class="mp-card__subtitle">
                          {{ session.subtitle2 }}
                        </p>
                      </template>
                      <div class="mp-card__meta">
                        <span class="mp-card__meta-item">
                          <i class="far fa-calendar" aria-hidden="true"></i>
                          {{ formatDate(session.startDate) }}
                        </span>
                        <!-- Agenda sessions: track/room -->
                        <span
                          v-if="
                            session.kind === 'agenda' &&
                            getRoomName(session, tracks, trackById) !== '—'
                          "
                          class="mp-card__track-pill"
                          :style="trackPillStyle(trackById(session.trackId))"
                        >
                          <i class="fas fa-door-open" aria-hidden="true"></i>
                          {{ getRoomName(session, tracks, trackById) }}
                        </span>
                        <span
                          v-if="
                            session.kind === 'agenda' &&
                            hasSeatLimit(fullSession(session))
                          "
                          class="mp-card__seats-pill"
                          :class="seatClass(fullSession(session))"
                        >
                          <i class="fas fa-chair" aria-hidden="true"></i>
                          {{ seatLabel(fullSession(session)) }}
                        </span>
                        <!-- Meetings / open sessions: sponsor or location -->
                        <span
                          v-if="session.kind !== 'agenda' && session.subtitle"
                          class="mp-card__meta-item"
                        >
                          <i class="fas fa-building" aria-hidden="true"></i>
                          {{ session.subtitle }}
                        </span>
                      </div>
                      <div class="mp-card__actions">
                        <button
                          v-if="
                            session.kind === 'agenda' &&
                            !isPhysicalEvent &&
                            isLive(session)
                          "
                          class="mp-card__join-btn"
                          @click="joinLiveStream(session)"
                        >
                          <span class="mp-card__join-dot"></span>
                          {{ $t('Views.ProfilePage.joinNow') }}
                        </button>
                        <button
                          v-if="session.kind === 'agenda'"
                          type="button"
                          class="mp-card__details-btn"
                          @click.stop="openSessionDetails(session)"
                        >
                          {{ $t('Views.Program.viewDetails') }}
                          <i
                            class="fas fa-chevron-right"
                            aria-hidden="true"
                          ></i>
                        </button>
                        <SessionDocuments
                          v-if="session.kind === 'agenda'"
                          :session="session"
                        />
                        <span
                          v-if="isPassed(session)"
                          class="mp-card__ended-label"
                        >
                          {{
                            session.kind === 'meeting'
                              ? $t('Views.ProfilePage.meetingEnded')
                              : $t('Views.ProfilePage.sessionEnded')
                          }}
                        </span>
                      </div>
                    </div>

                    <!-- Remove button — agenda sessions only (meetings and open
                       sessions are managed from the Networking pages). -->
                    <button
                      v-if="session.kind === 'agenda'"
                      class="mp-card__remove"
                      :disabled="isBookSessionLoading"
                      @click="addOrDeleteBooksSesions(session)"
                      :aria-label="
                        $t('Views.ProfilePage.removeSessionFromProgram', {
                          title: session.title
                        })
                      "
                      :title="$t('Views.ProfilePage.removeFromProgram')"
                    >
                      <i class="fas fa-minus" aria-hidden="true"></i>
                    </button>

                    <!-- Deep-link affordance for meetings / open sessions -->
                    <span v-else class="mp-card__open-hint" aria-hidden="true">
                      <i class="fas fa-chevron-right"></i>
                    </span>
                  </div>
                </div>

                <!-- Session details popup (View details) -->
                <div
                  v-if="activeSession"
                  class="session-modal-backdrop"
                  @click.self="activeSession = null"
                >
                  <div class="session-modal">
                    <button
                      class="session-modal__close"
                      type="button"
                      @click="activeSession = null"
                      :aria-label="$t('Views.Stage.Agenda.close')"
                    >
                      <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                    <div class="session-modal__header">
                      <span class="session-modal__time">
                        {{ formatTime(activeSession.startDate) }} –
                        {{ formatTime(activeSession.endDate) }}
                      </span>
                      <h2 class="session-modal__title">
                        {{ activeSession.title }}
                      </h2>
                      <p
                        v-if="activeSession.subtitle"
                        class="session-modal__subtitle"
                      >
                        {{ activeSession.subtitle }}
                      </p>
                      <p
                        v-if="activeSession.subtitle2"
                        class="session-modal__subtitle"
                      >
                        {{ activeSession.subtitle2 }}
                      </p>
                      <p
                        v-if="speakerNames(activeSession)"
                        class="session-modal__speaker"
                      >
                        <i class="fas fa-microphone-alt" aria-hidden="true"></i>
                        <template v-for="(sp, si) in activeSession.speakers">
                          <span :key="'msep-' + si" v-if="si > 0">, </span>
                          <button
                            :key="sp.id || sp.UserID || sp.name || si"
                            type="button"
                            class="session-modal__speaker-btn"
                            @click.stop="openSpeakerModal(sp)"
                          >
                            {{
                              `${sp.name || ''}${
                                sp.lastName ? ' ' + sp.lastName : ''
                              }`.trim()
                            }}
                          </button>
                        </template>
                      </p>
                      <div class="session-modal__meta">
                        <span
                          v-if="
                            tracks &&
                            tracks.length > 1 &&
                            activeSession.trackId &&
                            activeSession.trackId !== '0' &&
                            trackById(activeSession.trackId) &&
                            !trackById(activeSession.trackId).hideTrackTitle
                          "
                          class="mp-card__track-pill"
                          :style="
                            trackPillStyle(trackById(activeSession.trackId))
                          "
                        >
                          <i class="fas fa-door-open" aria-hidden="true"></i>
                          {{ trackById(activeSession.trackId).title }}
                        </span>
                        <span
                          v-if="hasSeatLimit(activeSession)"
                          class="mp-card__seats-pill"
                          :class="seatClass(activeSession)"
                        >
                          <i class="fas fa-chair" aria-hidden="true"></i>
                          {{ seatLabel(activeSession) }}
                        </span>
                      </div>
                    </div>
                    <div
                      v-if="activeSession.description"
                      class="session-modal__description html-content"
                      v-html="activeSession.description"
                    />
                    <div class="session-modal__footer">
                      <button
                        v-if="!isPhysicalEvent && isLive(activeSession)"
                        type="button"
                        class="session-modal__join-btn"
                        @click="joinLiveStream(activeSession)"
                      >
                        <span class="mp-card__join-dot"></span>
                        {{ $t('Views.ProfilePage.joinNow') }}
                      </button>
                      <button
                        type="button"
                        class="session-modal__book-btn session-modal__book-btn--booked"
                        :disabled="isBookSessionLoading"
                        @click="
                          addOrDeleteBooksSesions(activeSession);
                          activeSession = null;
                        "
                      >
                        <i class="fas fa-minus-circle" aria-hidden="true"></i>
                        {{ $t('Views.Program.removeFromMyProgram') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </AgendasDataProvider>
          </BookSessionsDataProvider>
        </div>
        <!-- end TAB: My Programs -->

        <!-- Log out — always available below the tab content, so it's
             reachable regardless of which tab is open. -->
        <div v-if="isOwnProfile" class="profile-logout">
          <button class="btn-logout" @click="confirmLogout">
            <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
            {{ $t('Views.ProfilePage.logOut') }}
          </button>
        </div>
      </div>

      <div v-else class="profile-loading">
        <i
          class="fas fa-circle-notch fa-spin"
          style="font-size: 24px; color: var(--color-primary, #047873)"
        ></i>
      </div>

      <!-- ── Take-photo (camera) modal ────────────────────────────── -->
      <transition name="logout-fade">
        <div v-if="cameraOpen" class="cam-overlay" @click.self="closeCamera">
          <div
            class="cam-modal"
            role="dialog"
            aria-modal="true"
            :aria-label="$t('Views.ProfilePage.takeAPhoto')"
          >
            <h3 class="cam-modal__title">
              {{ $t('Views.ProfilePage.takeAPhoto') }}
            </h3>
            <div class="cam-modal__stage">
              <video
                v-show="!capturedPhoto"
                ref="camVideo"
                class="cam-modal__video"
                autoplay
                playsinline
                muted
              ></video>
              <img
                v-if="capturedPhoto"
                :src="capturedPhoto"
                class="cam-modal__preview"
                :alt="$t('Views.ProfilePage.capturedPhotoPreview')"
              />
              <div v-if="cameraError" class="cam-modal__error">
                {{ cameraError }}
              </div>
            </div>
            <div class="cam-modal__actions">
              <template v-if="!capturedPhoto">
                <button
                  type="button"
                  class="cam-btn cam-btn--primary"
                  :disabled="!cameraReady"
                  @click="capturePhoto"
                >
                  <i class="fas fa-camera"></i>
                  {{ $t('Views.ProfilePage.capture') }}
                </button>
                <button
                  type="button"
                  class="cam-btn cam-btn--ghost"
                  @click="closeCamera"
                >
                  {{ $t('Views.ProfilePage.cancel') }}
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="cam-btn cam-btn--primary"
                  @click="useCapturedPhoto"
                >
                  <i class="fas fa-check"></i>
                  {{ $t('Views.ProfilePage.usePhoto') }}
                </button>
                <button
                  type="button"
                  class="cam-btn cam-btn--ghost"
                  @click="capturedPhoto = null"
                >
                  {{ $t('Views.ProfilePage.retake') }}
                </button>
              </template>
            </div>
            <canvas ref="camCanvas" class="cam-modal__canvas"></canvas>
          </div>
        </div>
      </transition>

      <!-- ── Logout confirmation overlay ──────────────────────────── -->
      <transition name="logout-fade">
        <div
          v-if="logoutModalOpen"
          class="logout-overlay"
          @click.self="cancelLogout"
        >
          <div
            class="logout-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="logoutTitle"
          >
            <div class="logout-modal__icon-wrap">
              <div class="logout-modal__icon">
                <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
              </div>
            </div>
            <h2 id="logoutTitle" class="logout-modal__title">
              {{ $t('Views.ProfilePage.logoutTitle') }}
            </h2>
            <p class="logout-modal__message">
              {{ $t('Views.ProfilePage.logoutMessage') }}
            </p>
            <div class="logout-modal__actions">
              <button
                type="button"
                class="logout-btn logout-btn--cancel"
                @click="cancelLogout"
              >
                {{ $t('Views.ProfilePage.cancel') }}
              </button>
              <button
                type="button"
                class="logout-btn logout-btn--confirm"
                @click="performLogout"
                :disabled="loggingOut"
              >
                <i v-if="loggingOut" class="fas fa-circle-notch fa-spin"></i>
                <i v-else class="fas fa-sign-out-alt"></i>
                {{
                  loggingOut
                    ? $t('Views.ProfilePage.loggingOut')
                    : $t('Views.ProfilePage.confirmLogout')
                }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import Navbar from '@/components/shared/Navbar';
import Avatar from '@/components/shared/Avatar';
import SessionDocuments from '@/components/shared/SessionDocuments';
import EditProfile from '@/components/profile/EditProfile';
import MatchingProfileForm from '@/components/aiMatching/MatchingProfileForm.vue';
import EventBadge from '@/views/Lobby/EventBadge';
import SpeakerInfoModal from '@/components/SpeakerInfoModal';
import VoucherBalance from '@/components/profile/VoucherBalance';
import SeatingCard from '@/components/profile/SeatingCard';
import { mapGetters, mapActions } from 'vuex';
import participantVisibility from '@/mixins/participantVisibility';
import {
  isHybridEvent,
  resolveParticipationType,
  setStoredParticipationType
} from '@/helpers/redirection-helpers';

export default {
  name: 'ProfilePage',
  mixins: [participantVisibility],
  components: {
    Navbar,
    Avatar,
    MatchingProfileForm,
    SessionDocuments,
    SeatingCard
  },
  data() {
    return {
      // Which profile tab is showing: 'info' | 'matching' | 'programs'.
      activeTab: 'info',
      // Session shown in the My Programs "View details" popup.
      activeSession: null,
      participationType: resolveParticipationType(),
      logoutModalOpen: false,
      loggingOut: false,
      // Avatar photo controls
      avatarMenuOpen: false,
      cameraOpen: false,
      cameraReady: false,
      cameraError: '',
      capturedPhoto: null,
      // Optimistic preview of a just-uploaded/captured photo so the user
      // sees it immediately, without waiting for the server round-trip.
      localAvatarPreview: null,
      avatarRemoved: false,
      // Inline field editing (edit each detail in place, no modal).
      // `key` is the profile field being edited ('name' | 'Email' |
      // 'Jobtitle' | 'Company' | 'Phone'); null when nothing is open.
      inlineEdit: { key: null, value: '', first: '', last: '', saving: false }
    };
  },
  created() {
    // Hydration of the participation toggle is handled by the reactive
    // `resolvedParticipationType` watcher below — it runs immediately AND
    // re-runs once the profile and event details are available on a hard
    // refresh.

    // When viewing another participant's profile (?userId=...), make
    // sure the attendee list is loaded so we can resolve their details.
    if (!this.isOwnProfile && !this.allUsers.length) {
      this.$store
        .dispatch('Users/fetchUsers', {
          page: 1,
          pageSize: 100,
          search: '',
          groupId: ''
        })
        .catch(() => {});
    }
    // Back from the live-stream page: restore tab + digital/physical.
    this._restoredFromLive = this.restoreProfileReturn();
  },
  mounted() {
    if (!this._restoredFromLive && this.$route.hash === '#my-programs') {
      this.showMyPrograms();
    }
    // "My agenda" buttons in messages / info pages while already on this page
    // (see helpers/agenda-link-helper).
    this.$root.$on('profile:open-programs', this.showMyPrograms);
    // Close the avatar photo menu on any outside click.
    this._onDocClickAvatar = () => {
      this.avatarMenuOpen = false;
    };
    document.addEventListener('click', this._onDocClickAvatar);
  },
  beforeDestroy() {
    this.$root.$off('profile:open-programs', this.showMyPrograms);
    if (this._onDocClickAvatar) {
      document.removeEventListener('click', this._onDocClickAvatar);
    }
    this.stopCameraStream();
  },
  watch: {
    participationType(val) {
      // Persist the attendee's own choice so every screen (feature
      // toggles, lobby gating, post-login redirect) picks it up and it
      // survives a refresh. Skipped while the toggle is only being synced
      // to the resolved value, so a default is never stored as a choice.
      // Also guarded against writing before the real per-user key is known
      // (profile still loading → 'guest' key).
      if (this._syncingParticipation) return;
      if (this.profile && this.profile.UserID) {
        setStoredParticipationType(this.profile.UserID, val);
      }
    },
    // Keep the toggle on the resolved participation. On a hard refresh the
    // profile and event details load asynchronously, so the first value can
    // be a default; this re-syncs once they arrive.
    resolvedParticipationType: {
      immediate: true,
      handler(val) {
        // Returning from the live-stream page the event settings reload for
        // a moment; until the event is hybrid again the resolved value is a
        // transient default ('physical'), so keep the restored choice.
        if (this._restoredParticipation && !isHybridEvent()) return;
        if (val !== this.participationType) {
          this._syncingParticipation = true;
          this.participationType = val;
          this.$nextTick(() => {
            this._syncingParticipation = false;
          });
        }
      }
    }
  },
  computed: {
    ...mapGetters('Authentication', { profile: 'getUser' }),
    ...mapGetters('CurrentEvent', { getEvent: 'getEvent' }),
    ...mapGetters('CurrentEvent', ['features', 'participantVisibilityMode']),
    ...mapGetters('Agendas', { agendaSessions: 'getSessions' }),
    // Gates the "Settings" action (AI matching profile) — only shown when
    // the event has the AI matching feature enabled.
    isAiMatchingEnabled() {
      return !!(this.features || {}).aiMatchingEnabled;
    },
    // Tabs shown on the own-profile page. "Profile info" is always
    // present; the matching + programs tabs appear only when their
    // features are enabled (so a single-tab page just shows the info).
    profileTabs() {
      const tabs = [
        {
          key: 'info',
          label: this.$t('Views.ProfilePage.tabProfileInfo'),
          icon: 'far fa-id-card'
        }
      ];
      if (this.isAiMatchingEnabled) {
        tabs.push({
          key: 'matching',
          label: 'Matching profile',
          icon: 'fas fa-sparkles'
        });
      }
      if (this.isBookingsEnabled) {
        tabs.push({
          key: 'programs',
          label: this.$t('Views.ProfilePage.myPrograms'),
          icon: 'far fa-calendar-check'
        });
      }
      return tabs;
    },
    // Event Badge (QR ticket) + Vouchers — relocated from the navbar
    // menu into the Profile info tab. Same gating as the navbar used.
    isQrEnabled() {
      return !!(
        this.profile &&
        this.profile.TicketQrCode &&
        (this.features || {}).stageQrTicketsEnabled
      );
    },
    isVoucherEnabled() {
      return !!(
        this.profile && (this.features || {}).stageVoucherBalanceEnabled
      );
    },
    // Flat, start-time-sorted list for the My Programs panel: booked agenda
    // sessions + Confirmed AI meetings + registered open sessions. The merge +
    // normalization (each item tagged with `kind`) lives in the BookSessions
    // store getter; here we just flatten the per-day grouping into one list.
    mergedPrograms() {
      const grouped =
        this.$store.getters['BookSessions/mergedMyAgendaGroupedByDay'];
      const byDay = (grouped && grouped.sessions) || {};
      return Object.keys(byDay)
        .reduce((acc, day) => acc.concat(byDay[day]), [])
        .sort(
          (a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0)
        );
    },
    // ── Profile-visibility self-controls (own profile only) ─────────
    // Only meaningful in 'limited' mode: there the participant opts in to
    // being shown. In 'visible' mode everything is shown by default (no
    // choice to make) and in 'hidden' mode the list isn't shown at all —
    // so the toggles are hidden in both of those cases.
    isVisibilityControlShown() {
      const f = this.features || {};
      const listEnabled = !!(
        f.stageTabParticipants || f.webAppNavigationParticipants
      );
      return listEnabled && this.participantVisibilityMode === 'limited';
    },
    // The admin's "My agenda" (booking) toggle — gates the My Programs
    // section so it's hidden entirely when bookings are off.
    isBookingsEnabled() {
      return !!(this.features || {}).bookingsEnabled;
    },
    isProfileVisible() {
      return String(this.profile && this.profile.Hidden) === '0';
    },
    isContactVisible() {
      return this.profile && this.profile.EmailAndPhoneVisible === 'True';
    },
    // ── Whose profile to show ───────────────────────────────────────
    // Defaults to the logged-in user; when a ?userId= is present (e.g.
    // opened from the chat dock's "View profile"), shows that
    // participant instead.
    viewedUserId() {
      return this.$route.query.userId || this.$route.params.userId || null;
    },
    isOwnProfile() {
      if (!this.viewedUserId) return true;
      return String(this.viewedUserId) === String(this.profile?.UserID);
    },
    allUsers() {
      const items =
        (this.$store.state.Users && this.$store.state.Users.items) || [];
      return Array.isArray(items) ? items : Object.values(items);
    },
    displayedUser() {
      if (this.isOwnProfile) return this.profile || {};
      const id = String(this.viewedUserId);
      const fromUsers = this.allUsers.find((u) => u && String(u.UserID) === id);
      if (fromUsers) return fromUsers;
      // Fallback to the object the chat dock cached when the window was
      // opened (covers large events where the user isn't in the first
      // page of the Users list).
      try {
        const cached = this.$store.getters['Chat/getDockUser'](
          this.viewedUserId
        );
        if (cached) return cached;
      } catch (e) {
        /* Chat store getter unavailable — ignore */
      }
      return {};
    },
    displayedFullName() {
      const first = this.displayedUser?.Firstname || '';
      const last = this.displayedUser?.Lastname || '';
      return `${first} ${last}`.trim();
    },
    // Own profile stores phone as `Cell`; attendee records use `Phone`.
    displayedPhone() {
      return this.displayedUser?.Cell || this.displayedUser?.Phone || '';
    },
    // Avatar source — a freshly uploaded/captured photo wins so it shows
    // instantly; a just-removed photo forces the initials fallback.
    avatarImage() {
      if (this.avatarRemoved) return '';
      return this.localAvatarPreview || this.displayedUser.Picture || '';
    },
    profileFullName() {
      const first = this.profile?.Firstname || '';
      const last = this.profile?.Lastname || '';
      return `${first} ${last}`.trim();
    },
    resolvedParticipationType() {
      return resolveParticipationType();
    },
    // Physical (web-app) attendees can't "join" a session online.
    isPhysicalEvent() {
      return this.participationType === 'physical';
    },
    isHybridEvent() {
      return isHybridEvent();
    },
    agendaTimezoneMode() {
      return this.getEvent?.agendaTimezoneMode;
    },
    eventTimezone() {
      return this.getEvent?.timezone;
    }
  },
  methods: {
    // Switch to the My Programs tab and scroll it into view.
    showMyPrograms() {
      if (this.isBookingsEnabled) this.activeTab = 'programs';
      this.$nextTick(() => {
        setTimeout(() => {
          const el = document.getElementById('my-programs');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200);
      });
    },
    ...mapActions('Authentication', [
      'doLogout',
      'toggleProfileVisibility',
      'toggleEmailAndPhoneVisibility',
      'changeAvatar',
      'changeAvatarFromDataUrl',
      'removeAvatar'
    ]),
    // ── Profile photo: upload / take / remove ─────────────────────
    triggerAvatarUpload() {
      this.avatarMenuOpen = false;
      if (this.$refs.avatarInput) this.$refs.avatarInput.click();
    },
    onAvatarFileSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (e.target) e.target.value = '';
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.$notify({
          group: 'default',
          title: this.$t('Views.ProfilePage.chooseImageFile')
        });
        return;
      }
      // Show the picked image right away.
      const reader = new FileReader();
      reader.onload = () => {
        this.avatarRemoved = false;
        this.localAvatarPreview = reader.result;
      };
      reader.readAsDataURL(file);
      this.saveAvatar(this.changeAvatar(file));
    },
    // Upload the picked/captured photo. On failure, drop the optimistic
    // preview so the avatar doesn't look saved when it isn't.
    saveAvatar(upload) {
      Promise.resolve(upload)
        .then((response) => {
          // The API answers 200 with `false` when it couldn't store the image.
          if (response && response.data === false) throw new Error('not saved');
          this.$notify({
            group: 'default',
            title: this.$t('Views.ProfilePage.photoUpdated')
          });
        })
        .catch(() => {
          this.localAvatarPreview = null;
          this.$notify({
            group: 'default',
            type: 'error',
            title: this.$t('Views.ProfilePage.photoUploadFailed')
          });
        });
    },
    onRemoveAvatar() {
      this.avatarMenuOpen = false;
      if (
        typeof window !== 'undefined' &&
        !window.confirm(this.$t('Views.ProfilePage.confirmRemovePhoto'))
      ) {
        return;
      }
      this.localAvatarPreview = null;
      this.avatarRemoved = true;
      Promise.resolve(this.removeAvatar())
        .then(() =>
          this.$notify({
            group: 'default',
            title: this.$t('Views.ProfilePage.photoRemoved')
          })
        )
        .catch(() => {});
    },
    // ── Camera capture ────────────────────────────────────────────
    async openCamera() {
      this.avatarMenuOpen = false;
      this.capturedPhoto = null;
      this.cameraError = '';
      this.cameraReady = false;
      this.cameraOpen = true;
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('unsupported');
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: false
        });
        this._camStream = stream;
        await this.$nextTick();
        const video = this.$refs.camVideo;
        if (video) {
          video.srcObject = stream;
          video.onloadedmetadata = () => {
            this.cameraReady = true;
          };
        }
      } catch (e) {
        this.cameraError = this.$t('Views.ProfilePage.cameraError');
      }
    },
    capturePhoto() {
      const video = this.$refs.camVideo;
      const canvas = this.$refs.camCanvas;
      if (!video || !canvas || !video.videoWidth) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      this.capturedPhoto = canvas.toDataURL('image/jpeg', 0.9);
    },
    useCapturedPhoto() {
      const dataUrl = this.capturedPhoto;
      if (!dataUrl) return;
      // Show it immediately.
      this.avatarRemoved = false;
      this.localAvatarPreview = dataUrl;
      this.saveAvatar(this.changeAvatarFromDataUrl(dataUrl));
      this.closeCamera();
    },
    stopCameraStream() {
      if (this._camStream) {
        this._camStream.getTracks().forEach((t) => t.stop());
        this._camStream = null;
      }
    },
    closeCamera() {
      this.stopCameraStream();
      this.cameraOpen = false;
      this.capturedPhoto = null;
      this.cameraReady = false;
      this.cameraError = '';
    },
    // These endpoints flip the value server-side and refresh the user,
    // so the toggle UI updates from the new `profile` automatically.
    onToggleProfileVisible() {
      this.toggleProfileVisibility(!this.isProfileVisible);
    },
    onToggleContactVisible() {
      if (!this.isProfileVisible) return;
      this.toggleEmailAndPhoneVisibility(!this.isContactVisible);
    },
    // Deep-link a merged "My Programs" item to the page that owns it.
    // Agenda sessions are handled in-place (join / remove); meetings and
    // open sessions jump to the Networking pages where they're managed.
    openProgram(session) {
      if (!session) return;
      if (session.kind === 'meeting') {
        this.$router.push({ name: 'my-meetings' }).catch(() => {});
      } else if (session.kind === 'openSession') {
        this.$router.push({ name: 'open-sessions' }).catch(() => {});
      }
    },
    // ── Inline field editing (edit details in place, no modal) ──────
    startInlineEdit(key) {
      const u = this.displayedUser || {};
      this.inlineEdit.key = key;
      this.inlineEdit.saving = false;
      if (key === 'name') {
        this.inlineEdit.first = u.Firstname || '';
        this.inlineEdit.last = u.Lastname || '';
      } else if (key === 'Phone') {
        this.inlineEdit.value = u.Cell || u.Phone || '';
      } else {
        this.inlineEdit.value = u[key] || '';
      }
      this.$nextTick(() => {
        const el = this.$refs.inlineInput;
        const node = Array.isArray(el) ? el[0] : el;
        if (node && node.focus) node.focus();
      });
    },
    cancelInlineEdit() {
      this.inlineEdit.key = null;
    },
    async saveInlineEdit() {
      if (this.inlineEdit.saving) return;
      const u = this.displayedUser || {};
      // Send the full set (like the edit modal) with the one changed field,
      // so the others aren't wiped by the update.
      const payload = {
        Firstname: u.Firstname,
        Lastname: u.Lastname,
        About2: u.About2,
        Phone: u.Cell || u.Phone,
        Company: u.Company,
        Jobtitle: u.Jobtitle,
        Email: u.Email,
        Address1: u.Address1
      };
      if (this.inlineEdit.key === 'name') {
        payload.Firstname = this.inlineEdit.first.trim();
        payload.Lastname = this.inlineEdit.last.trim();
      } else if (this.inlineEdit.key === 'Phone') {
        payload.Phone = this.inlineEdit.value.trim();
      } else {
        payload[this.inlineEdit.key] = this.inlineEdit.value.trim();
      }
      this.inlineEdit.saving = true;
      try {
        await this.$store.dispatch('Authentication/updateUser', payload);
        this.$notify({
          group: 'default',
          title: this.$t('Views.Stage.MyProfile.saved')
        });
        this.inlineEdit.key = null;
      } catch (e) {
        this.$notify({
          group: 'default',
          type: 'error',
          title: this.$t('Views.ProfilePage.saveFailed')
        });
      } finally {
        this.inlineEdit.saving = false;
      }
    },
    // ── Event Badge / Vouchers (relocated from the navbar menu) ─────
    openEventBadgeModal() {
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        EventBadge,
        {},
        {
          adaptive: true,
          clickToClose: true,
          width: '92%',
          height: 'auto',
          minWidth: 300,
          maxWidth: 480
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    },
    openVoucherModal() {
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        VoucherBalance,
        {},
        {
          adaptive: true,
          clickToClose: true,
          width: '92%',
          height: 'auto',
          minWidth: 300,
          maxWidth: 520
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    },
    openEditProfile() {
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        EditProfile,
        {},
        {
          adaptive: true,
          minWidth: 320,
          maxWidth: 720,
          width: '90%',
          height: 'auto'
        }
      );
    },
    confirmLogout() {
      this.logoutModalOpen = true;
      // Lock the page scroll while the overlay is up
      document.body.classList.add('overflow-hidden');
    },
    cancelLogout() {
      if (this.loggingOut) return;
      this.logoutModalOpen = false;
      document.body.classList.remove('overflow-hidden');
    },
    performLogout() {
      if (this.loggingOut) return;
      this.loggingOut = true;
      // doLogout clears the stored token, closes pusher, commits Logout
      // and routes to the login page (then reloads to reset Vuex state).
      this.doLogout();
    },

    // ── Time / date formatting — same logic as Agenda.vue ─────────
    formatTime(dateStr) {
      if (!dateStr) return '';
      if (this.agendaTimezoneMode === 2) {
        return new Intl.DateTimeFormat('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: this.eventTimezone
        }).format(new Date(dateStr));
      }
      return this.$moment.utc(dateStr).local().format('HH:mm');
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return this.$moment(dateStr).format('MMM D');
    },

    speakerNames(session) {
      if (!session.speakers || !session.speakers.length) return '';
      return session.speakers
        .map((s) => `${s.name || ''} ${s.lastName || ''}`.trim())
        .filter(Boolean)
        .join(', ');
    },

    getRoomName(session, tracks, trackById) {
      if (!tracks || tracks.length <= 1) return '—';
      if (!session.trackId || session.trackId === '0') return '—';
      const track = trackById(session.trackId);
      if (!track || track.hideTrackTitle) return '—';
      return track.title || '—';
    },

    // ── Status logic — same UTC parsing as Program.vue / Agenda.vue ──
    parseSessionDate(date) {
      if (!date) return null;
      if (
        this.agendaTimezoneMode === 2 &&
        this.eventTimezone &&
        this.$moment.tz
      ) {
        return this.$moment.tz(date, this.eventTimezone);
      }
      return this.$moment.utc(date).local();
    },
    isPassed(session) {
      if (!session || !session.endDate) return false;
      return this.parseSessionDate(session.endDate).isBefore(this.$moment());
    },
    isLive(session) {
      if (!session || !session.startDate || !session.endDate) return false;
      const now = this.$moment();
      return (
        now.isSameOrAfter(this.parseSessionDate(session.startDate)) &&
        now.isBefore(this.parseSessionDate(session.endDate))
      );
    },
    // The booked-sessions list can carry a trimmed session; the agenda
    // store has the full one (speakers, description, seats, track).
    fullSession(session) {
      if (!session) return session;
      const full = (this.agendaSessions || []).find(
        (x) => x && x.id === session.id
      );
      return full ? { ...session, ...full } : session;
    },
    openSessionDetails(session) {
      this.activeSession = this.fullSession(session);
    },
    // ── Track pill colour (same rules as Program.vue) ──────────────
    trackPillStyle(track) {
      const bg = (track && track.headerBackgroundColor) || '';
      if (!bg) return null;
      return {
        backgroundColor: bg,
        color: this.pickContrastingTextColor(bg),
        borderColor: bg
      };
    },
    pickContrastingTextColor(bg) {
      const hex = String(bg).trim().replace(/^#/, '');
      const full =
        hex.length === 3
          ? hex
              .split('')
              .map((c) => c + c)
              .join('')
          : hex.length === 6
          ? hex
          : null;
      if (!full) return '#ffffff';
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return lum > 0.62 ? '#111827' : '#ffffff';
    },
    // ── Seat availability (same rules as Program.vue) ──────────────
    hasSeatLimit(session) {
      return !!(session && session.myAgendaSize && session.myAgendaSize > 0);
    },
    seatsLeftCount(session) {
      const booked = (session.userIds && session.userIds.length) || 0;
      return Math.max(0, (session.myAgendaSize || 0) - booked);
    },
    seatLabel(session) {
      const left = this.seatsLeftCount(session);
      const total = session.myAgendaSize || 0;
      if (left <= 0) return this.$t('Views.Program.seatsFull');
      return this.$tc('Views.Program.seatsLeft', left, { left, total });
    },
    seatClass(session) {
      const left = this.seatsLeftCount(session);
      const total = session.myAgendaSize || 0;
      if (left <= 0) return 'mp-card__seats--full';
      if (total > 0 && left / total <= 0.2) return 'mp-card__seats--low';
      return 'mp-card__seats--ok';
    },
    openSpeakerModal(speaker) {
      if (!speaker) return;
      this.$modal.show(
        SpeakerInfoModal,
        { speaker },
        {
          width: '92%',
          height: 'auto',
          adaptive: true,
          minWidth: 300,
          maxWidth: 640
        }
      );
    },
    joinLiveStream(session) {
      if (!session) return;
      this.$store.commit('Stream/setStreamUrl', session.liveUrl || '');
      this.$store.commit('Stream/setSessionId', session.id);
      this.$store.commit('Stream/setStreamTitle', session.title || '');
      // Remember where we were (My Programs tab, scroll, digital/physical)
      // so the back button returns here instead of a reset page.
      // Persist the current platform explicitly so nothing re-derives a
      // default (physical on phones) while the page reloads on return.
      if (this.profile && this.profile.UserID && isHybridEvent()) {
        setStoredParticipationType(
          this.profile.UserID,
          resolveParticipationType()
        );
      }
      try {
        sessionStorage.setItem(
          'tappin_profile_return',
          JSON.stringify({
            tab: this.activeTab,
            y: window.pageYOffset || 0,
            participation: resolveParticipationType()
          })
        );
      } catch (e) {
        /* storage unavailable — back just lands on the page */
      }
      this.$router.push({
        name: 'live-stream',
        query: { session: session.id }
      });
    },
    // Coming back from the live-stream page: re-open the tab we left from,
    // keep the attendee on the platform they left (digital stays digital)
    // and scroll back to where they were.
    restoreProfileReturn() {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem('tappin_profile_return'));
        sessionStorage.removeItem('tappin_profile_return');
      } catch (e) {
        saved = null;
      }
      if (!saved) return false;
      if (saved.participation) {
        const uid = this.profile && this.profile.UserID;
        if (uid) setStoredParticipationType(uid, saved.participation);
        this._restoredParticipation = saved.participation;
        this._syncingParticipation = true;
        this.participationType = saved.participation;
        this.$nextTick(() => {
          this._syncingParticipation = false;
        });
      }
      if (saved.tab) this.activeTab = saved.tab;
      const y = saved.y || 0;
      let tries = 0;
      const tick = () => {
        window.scrollTo({ top: y });
        tries += 1;
        if (Math.abs((window.pageYOffset || 0) - y) >= 4 && tries < 30) {
          setTimeout(tick, 100);
        }
      };
      this.$nextTick(tick);
      return true;
    },
    statusLabel(session) {
      if (this.isLive(session)) return this.$t('Views.ProfilePage.statusLive');
      if (this.isPassed(session))
        return this.$t('Views.ProfilePage.statusEnded');
      return this.$t('Views.ProfilePage.statusScheduled');
    },
    statusKey(session) {
      if (this.isLive(session)) return 'live';
      if (this.isPassed(session)) return 'ended';
      return 'scheduled';
    },
    statusClass(session) {
      if (this.isLive(session)) return 'status-badge--live';
      if (this.isPassed(session)) return 'status-badge--ended';
      return 'status-badge--scheduled';
    },

    sortedSessions(bookSessions) {
      return bookSessions
        .slice()
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    },

    // ── Export to CSV ─────────────────────────────────────────────
    exportPrograms(bookSessions, trackById) {
      if (!bookSessions || !bookSessions.length) return;
      const rows = [
        [
          this.$t('Views.ProfilePage.csvProgram'),
          this.$t('Views.ProfilePage.csvSpeaker'),
          this.$t('Views.ProfilePage.csvDate'),
          this.$t('Views.ProfilePage.csvTime'),
          this.$t('Views.ProfilePage.csvRoom'),
          this.$t('Views.ProfilePage.csvStatus')
        ]
      ];
      this.sortedSessions(bookSessions).forEach((s) => {
        const track = s.trackId && trackById ? trackById(s.trackId) : null;
        rows.push([
          s.title || '',
          this.speakerNames(s),
          this.$moment(s.startDate).format('YYYY-MM-DD'),
          `${this.formatTime(s.startDate)} - ${this.formatTime(s.endDate)}`,
          (track && track.title) || '',
          this.statusLabel(s)
        ]);
      });
      const csv = rows
        .map((r) =>
          r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'my-programs.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped lang="scss">
.profile-page {
  font-family: 'Poppins', sans-serif;

  *:focus-visible {
    outline: 2px solid var(--color-primary, #047873);
    outline-offset: 2px;
  }
}

// Inline edit pencil after editable details (name + contact fields)
.field-edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
  font-size: 12px;
  line-height: 1;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s;
}
.field-edit:hover {
  color: var(--color-primary, #047873);
  background: #f3f4f6;
}
// In the contact rows the pencil sits at the far right
.contact-item .field-edit {
  margin-left: auto;
}

// Inline edit inputs (shown in place when a pencil is clicked)
.field-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.contact-item .field-input {
  flex: 1 1 auto;
  min-width: 0;
}
.field-input {
  padding: 6px 10px;
  border: 1.5px solid var(--color-primary, #047873);
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  color: #111827;
  background: #ffffff;
  outline: none;
}
.field-save,
.field-cancel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}
.field-save {
  background: var(--color-primary, #047873);
  color: #ffffff;
}
.field-save:disabled {
  opacity: 0.6;
  cursor: default;
}
.field-cancel {
  background: #f3f4f6;
  color: #6b7280;
}
.field-cancel:hover {
  background: #e5e7eb;
  color: #111827;
}
.contact-item__text--empty {
  color: #9ca3af;
  font-style: italic;
}

// Log out pinned at the bottom of the profile page
.profile-logout {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;

  // On mobile the floating bottom-nav pill overlaps the page bottom, so
  // give the log-out button room to sit clear of it (the page wrapper
  // already adds ~40px, so this brings the total clearance to ~100px).
  @media (max-width: 640px) {
    margin-bottom: 64px;
  }
}

.profile-wrap {
  max-width: 1100px;
  margin: 0 auto;
  padding: 28px 24px 60px;

  @media (max-width: 640px) {
    padding: 18px 14px 40px;
  }
}

// Inline networking (matching profile) form — same vertical rhythm as
// the other profile sections.
.profile-networking {
  margin-bottom: 32px;
}

// ── Profile tabs ─────────────────────────────────────────────────────
.profile-tabs {
  display: flex;
  gap: 2px;
  margin-bottom: 26px;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.profile-tab {
  appearance: none;
  border: none;
  background: none;
  font-family: 'Poppins', sans-serif;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px; // overlap the 1px baseline
  transition: color 0.15s, border-color 0.15s;

  i {
    font-size: 14px;
  }

  &:hover {
    color: #111827;
  }

  &--active {
    color: var(--color-primary, #047873);
    border-bottom-color: var(--color-primary, #047873);
  }

  @media (max-width: 640px) {
    padding: 11px 12px;
    font-size: 13px;
  }
}

// ── Profile action tiles (Event Badge / Vouchers) ────────────────────
.profile-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    margin-bottom: 22px;
  }
}

.action-tile {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 18px 20px;
  cursor: pointer;
  text-align: left;
  font-family: 'Poppins', sans-serif;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  &__icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;

    &--badge {
      background: #f3f0ff;
      color: #7c3aed;
    }
    &--voucher {
      background: #fff4ed;
      color: #ea580c;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  &__desc {
    font-size: 12.5px;
    color: #6b7280;
    margin-top: 2px;
  }

  &__chev {
    margin-left: auto;
    color: #9ca3af;
    font-size: 13px;
  }
}

// Dark mode for tabs + action tiles
body[data-theme='dark'] {
  .profile-tabs {
    border-bottom-color: var(--t-border);
  }
  .profile-tab {
    color: var(--t-text-secondary);
    &:hover {
      color: var(--t-text);
    }
  }
  .action-tile {
    background: var(--t-surface);
    border-color: var(--t-border);
    box-shadow: var(--t-card-shadow, 0 1px 4px rgba(0, 0, 0, 0.3));
    &__title {
      color: var(--t-text);
    }
    &__desc {
      color: var(--t-text-secondary);
    }
    &:hover {
      border-color: var(--color-primary, #047873);
    }
  }
}

.profile-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

// ── Header ───────────────────────────────────────────────────────────
.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  gap: 12px;
  flex-wrap: wrap;

  &__title {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    margin: 0;

    @media (max-width: 640px) {
      font-size: 22px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
}

.btn-logout {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 6px 12px;
  background: #ffffff;
  border: 1.5px solid #fecaca;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #dc2626;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  i {
    font-size: 11px;
  }

  &:hover {
    background: #fee2e2;
    border-color: #ef4444;
    color: #b91c1c;
  }
}

.btn-edit {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 6px 12px;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
    color: var(--color-primary, #047873);
  }

  i {
    font-size: 11px;
  }
}

// ── Profile card ─────────────────────────────────────────────────────
.profile-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 28px 28px 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 32px;

  @media (max-width: 640px) {
    padding: 20px 18px 18px;
    margin-bottom: 22px;
    border-radius: 14px;
  }

  &__top {
    display: flex;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 28px;

    @media (max-width: 640px) {
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 14px;
      margin-bottom: 22px;
    }
  }

  &__name-block {
    flex: 1;
    padding-top: 12px;

    @media (max-width: 640px) {
      padding-top: 0;
    }
  }

  &__name {
    font-size: 26px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 10px;
    line-height: 1.2;

    @media (max-width: 640px) {
      font-size: 20px;
    }
  }

  &__tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;

    @media (max-width: 640px) {
      justify-content: center;
    }
  }
}

.avatar-wrap {
  position: relative;
  width: 120px;
  height: 120px;
  flex-shrink: 0;

  &__img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    background: #e5e7eb;
  }

  ::v-deep .avatar {
    width: 100% !important;
    height: 100% !important;
    border-radius: 50%;
    overflow: hidden;

    img {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover;
    }
  }

  &__badge {
    position: absolute;
    bottom: 4px;
    right: 4px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid #ffffff;
    font-size: 14px;
  }

  // Camera button (own profile) — opens the photo options menu.
  &__edit {
    position: absolute;
    bottom: 4px;
    right: 4px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid #ffffff;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.15s, transform 0.15s;

    &:hover {
      background: var(--color-primary-hover, #036663);
      transform: scale(1.05);
    }
  }
}

// Photo options dropdown
.avatar-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 20;
  min-width: 210px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);
  padding: 6px;

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 12px;
    border: none;
    background: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    text-align: left;
    font-family: 'Poppins', sans-serif;

    i {
      width: 16px;
      font-size: 13px;
      color: #6b7280;
    }

    &:hover {
      background: #f3f4f6;
    }

    &--danger {
      color: #dc2626;
      i {
        color: #dc2626;
      }
      &:hover {
        background: #fee2e2;
      }
    }
  }
}

.avatar-hidden-file {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}

// ── Camera capture modal ──────────────────────────────────────────
.cam-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.cam-modal {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.32);

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 14px;
    text-align: center;
  }

  &__stage {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 12px;
    overflow: hidden;
    background: #0f172a;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__video,
  &__preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__error {
    padding: 24px;
    color: #fca5a5;
    font-size: 13px;
    text-align: center;
  }

  &__canvas {
    display: none;
  }

  &__actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 16px;
  }
}

.cam-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  border: 1.5px solid transparent;

  &--primary {
    background: var(--color-primary, #047873);
    color: #ffffff;

    &:hover:not(:disabled) {
      background: var(--color-primary-hover, #036663);
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &--ghost {
    background: #ffffff;
    color: #374151;
    border-color: #e5e7eb;

    &:hover {
      border-color: var(--color-primary, #047873);
      color: var(--color-primary, #047873);
    }
  }
}

// Dark mode for the photo menu + camera modal
body[data-theme='dark'] {
  .avatar-menu {
    background: var(--t-surface-elevated);
    border-color: var(--t-border-strong);
    box-shadow: var(--t-card-shadow-lg);
  }
  .avatar-menu__item {
    color: var(--t-text-secondary);
    i {
      color: var(--t-text-muted);
    }
    &:hover {
      background: var(--t-surface-alt);
    }
    &--danger {
      color: var(--t-danger);
      i {
        color: var(--t-danger);
      }
      &:hover {
        background: var(--t-danger-soft);
      }
    }
  }
  .cam-modal {
    background: var(--t-surface);

    &__title {
      color: var(--t-text);
    }
  }
  .cam-btn--ghost {
    background: var(--t-surface-alt);
    color: var(--t-text-secondary);
    border-color: var(--t-border);
  }
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;

  &--filled {
    background: var(--color-primary, #047873);
    color: #ffffff;
  }

  &--outline {
    background: #ffffff;
    color: #6b7280;
    border: 1.5px solid #e5e7eb;
  }
}

// ── Contact grid ─────────────────────────────────────────────────────
.contact-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px 24px;
  margin-bottom: 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 14px;

  &__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;

    &--teal {
      background: #e6f7f5;
      color: var(--color-primary, #047873);
    }
    &--purple {
      background: #f3f0ff;
      color: #7c3aed;
    }
    &--orange {
      background: #fff4ed;
      color: #ea580c;
    }
    &--green {
      background: #ecfdf5;
      color: #16a34a;
    }
  }

  &__text {
    font-size: 14px;
    color: #374151;
    word-break: break-word;
  }
}

// ── Participation box ────────────────────────────────────────────────
.participate-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 20px;
  margin: 0; /* reset fieldset default */

  &__label {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 10px;
  }

  &__options {
    display: flex;
    gap: 28px;
  }
}

// ── Profile visibility self-controls ──────────────────────────────
.visibility-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 20px;
  margin: 14px 0 0;

  &__label {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
  }
}

.visibility-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  & + & {
    margin-top: 14px;
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
  }

  &__desc {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }
}

// Toggle switch (matches the notification-settings switch).
.vis-toggle {
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &__track {
    display: inline-flex;
    align-items: center;
    width: 40px;
    height: 23px;
    padding: 2px;
    border-radius: 9999px;
    background: #d1d5db;
    transition: background 0.18s;
  }

  &__thumb {
    width: 19px;
    height: 19px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: transform 0.18s;
  }

  &--on .vis-toggle__track {
    background: var(--color-primary, #047873);
  }
  &--on .vis-toggle__thumb {
    transform: translateX(17px);
  }
}

body[data-theme='dark'] {
  .visibility-box {
    background: var(--t-surface-alt);
    border-color: var(--t-border);
  }
  .visibility-box__label {
    color: var(--t-text);
  }
  .visibility-row__title {
    color: var(--t-text);
  }
  .visibility-row__desc {
    color: var(--t-text-secondary);
  }
  .vis-toggle__track {
    background: var(--t-input-border);
  }
  .vis-toggle--on .vis-toggle__track {
    background: var(--t-btn);
  }
}

.radio-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;

  input[type='radio'] {
    display: none;
  }

  &__dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #d1d5db;
    position: relative;
    transition: border-color 0.15s;

    &::after {
      content: '';
      position: absolute;
      inset: 3px;
      border-radius: 50%;
      background: var(--color-primary, #047873);
      transform: scale(0);
      transition: transform 0.15s;
    }
  }

  input:checked ~ &__dot {
    border-color: var(--color-primary, #047873);

    &::after {
      transform: scale(1);
    }
  }
}

// ── My Programs section ──────────────────────────────────────────────
.my-programs {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 24px 28px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  @media (max-width: 640px) {
    padding: 18px 14px;
    border-radius: 14px;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 18px;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    color: #111827;
    margin: 0;

    @media (max-width: 640px) {
      font-size: 18px;
    }
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 24px;
    padding: 0 8px;
    border-radius: 9999px;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 13px;
    font-weight: 600;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #6b7280;
    font-size: 14px;
  }
}

// ── My Programs session cards (matching Program.vue design) ─────────
.my-programs__grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mp-card {
  display: flex;
  align-items: stretch;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
  min-width: 0;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
  }

  // Meetings / open sessions deep-link to their Networking page on click.
  &--clickable {
    cursor: pointer;

    &:hover .mp-card__open-hint {
      color: var(--color-primary, #047873);
      transform: translateX(2px);
    }
  }

  &__open-hint {
    position: absolute;
    top: 12px;
    right: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 13px;
    transition: color 0.15s, transform 0.15s;
  }

  &--ended {
    background: #f9fafb;
    opacity: 0.78;

    .mp-card__title,
    .mp-card__start {
      color: #6b7280;
    }

    .mp-card__end,
    .mp-card__speaker,
    .mp-card__meta-item {
      color: #6b7280;
    }

    .mp-card__clock {
      color: #6b7280;
    }

    .mp-card__status {
      background: #f3f4f6 !important;
      color: #6b7280 !important;
      border-color: #e5e7eb !important;
    }
  }

  &__time-col {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 20px 16px;
    min-width: 110px;
    border-right: 1px solid #f3f4f6;
    gap: 4px;

    @media (max-width: 480px) {
      padding: 16px 12px;
      min-width: 92px;
    }
  }

  &__clock {
    font-size: 13px;
    color: var(--color-primary, #047873);
    margin-bottom: 4px;
  }

  &__start {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    line-height: 1;
  }

  &__end {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.2;
    margin-bottom: 8px;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;

    &--live {
      background: #ecfdf5;
      color: #16a34a;
      border: 1.5px solid #d1fae5;
    }

    &--scheduled {
      border: 1.5px solid var(--color-primary, #047873);
      color: var(--color-primary, #047873);
      background: transparent;
    }

    &--ended {
      border: 1.5px solid #d1d5db;
      color: #6b7280;
      background: transparent;
    }
  }

  &__live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #16a34a;
    box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.25);
    animation: mp-pulse-live 1.6s ease-in-out infinite;
  }

  &__body {
    flex: 1;
    padding: 20px 16px 20px 20px;
    min-width: 0;

    @media (max-width: 480px) {
      padding: 16px 12px 16px 14px;
    }
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 4px;
    line-height: 1.3;
  }

  &__subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 4px;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }

  // Kind badge for non-agenda programs (meetings / open sessions)
  &__kind {
    display: inline-block;
    vertical-align: middle;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    line-height: 1.4;

    &--meeting {
      background: #e6f5f4; // teal-50 (platform)
      color: #036663; // teal-700
    }

    &--open {
      background: #f3e8ff; // light purple
      color: #7e22ce;
    }
  }

  &__speaker {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 10px;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    i {
      font-size: 12px;
      color: #6b7280;
    }
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-bottom: 14px;
  }

  &__meta-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 14px;
    color: #6b7280;

    i {
      font-size: 12px;
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }

  &__join-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 14px;
    background: #16a34a;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    transition: background 0.15s;

    &:hover {
      background: #15803d;
    }
  }

  &__join-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
    animation: mp-pulse-live 1.6s ease-in-out infinite;
  }

  &__ended-label {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    background: #f3f4f6;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
  }

  // Remove button — floated top-right of the card
  &__remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background: #f3f4f6;
    color: #374151;
    cursor: pointer;
    flex-shrink: 0;
    align-self: flex-start;
    margin: 16px 16px 0 0;
    font-size: 14px;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: #fee2e2;
      color: #dc2626;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

@keyframes mp-pulse-live {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.7;
  }
}

.btn-export {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
    color: var(--color-primary, #047873);
  }

  i {
    font-size: 13px;
  }
}

// ── Logout confirmation overlay ─────────────────────────────────────
.logout-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.55);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 20px;
  font-family: 'Poppins', sans-serif;
}

.logout-modal {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.18),
    0 10px 15px -3px rgb(0 0 0 / 0.1);
  text-align: center;

  &__icon-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
  }

  &__icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #fee2e2;
    color: #dc2626;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    box-shadow: 0 0 0 8px rgba(254, 226, 226, 0.5);
  }

  &__title {
    font-size: 19px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px;
    line-height: 1.3;
  }

  &__message {
    font-size: 13px;
    color: #6b7280;
    margin: 0 0 22px;
    line-height: 1.55;
  }

  &__actions {
    display: flex;
    gap: 10px;
    justify-content: center;

    @media (max-width: 480px) {
      flex-direction: column-reverse;
    }
  }
}

.logout-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 11px 22px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s;
  flex: 1;

  i {
    font-size: 12px;
  }

  &--cancel {
    background: #ffffff;
    color: #374151;
    border: 1.5px solid #d1d5db;

    &:hover {
      background: #f9fafb;
      border-color: var(--color-primary, #047873);
      color: var(--color-primary, #047873);
    }
  }

  &--confirm {
    background: #dc2626;
    color: #ffffff;
    border: none;

    &:hover {
      background: #b91c1c;
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.logout-fade-enter-active,
.logout-fade-leave-active {
  transition: opacity 0.18s ease;

  .logout-modal {
    transition: transform 0.18s ease, opacity 0.18s ease;
  }
}

.logout-fade-enter,
.logout-fade-leave-to {
  opacity: 0;

  .logout-modal {
    transform: translateY(8px) scale(0.97);
    opacity: 0;
  }
}

.mp-card__details-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: #ffffff;
  color: var(--color-primary, #047873);
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  font-family: 'Poppins', sans-serif;

  i {
    font-size: 10px;
  }

  &:hover {
    background: #f9fafb;
    border-color: var(--color-primary, #047873);
  }
}

// ── Session modal ─────────────────────────────────────────────────────────
.session-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.session-modal {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 28px;

  &__close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: #f3f4f6;
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;

    &:hover {
      background: #e5e7eb;
    }
  }

  &__header {
    margin-bottom: 20px;
    padding-right: 32px;
  }

  &__time {
    font-size: 14px;
    color: var(--color-primary, #047873);
    font-weight: 600;
    display: block;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 6px;
  }

  &__subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 10px;
  }

  &__speaker {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0 6px;
    font-size: 14px;
    color: #374151;
    margin-bottom: 10px;

    i {
      color: #6b7280;
      margin-right: 2px;
    }
  }

  // Clickable speaker name — opens that speaker's detail popup.
  &__speaker-btn {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-weight: 600;
    color: var(--color-primary, #047873);
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover,
    &:focus-visible {
      text-decoration-thickness: 2px;
      outline: none;
    }
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 14px;
    color: #6b7280;

    span {
      display: inline-flex;
      align-items: center;
      gap: 5px;

      i {
        font-size: 12px;
      }
    }
  }

  &__description {
    font-size: 14px;
    color: #374151;
    line-height: 1.7;
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;
    margin-bottom: 20px;
  }

  &__footer {
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
  }

  &__join-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 22px;
    background: #16a34a;
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    transition: opacity 0.15s;

    &:hover {
      opacity: 0.9;
    }
  }

  &__book-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: var(--color-primary, #047873);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    font-family: 'Poppins', sans-serif;

    &:hover {
      opacity: 0.85;
    }

    &--booked {
      background: #f3f4f6;
      color: #374151;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.mp-card__track-pill,
.mp-card__seats-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 12.5px;
  line-height: 1.4;
  border: 1px solid transparent;
  gap: 6px;
  white-space: nowrap;

  i {
    font-size: 11px;
    color: inherit;
  }
}
.mp-card__track-pill {
  background: #f3f4f6;
  color: #374151;
  // Long track names (e.g. parallel sessions) wrap inside the pill
  // instead of being clipped by the card. Short names stay on one line.
  white-space: normal;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  align-items: flex-start;
  border-radius: 14px;

  i {
    flex-shrink: 0;
    margin-top: 3px;
  }
}
.mp-card__seats-pill {
  &.mp-card__seats--ok {
    background: rgba(21, 128, 61, 0.12);
    color: #15803d;
    border-color: rgba(21, 128, 61, 0.25);
  }
  &.mp-card__seats--low {
    background: rgba(180, 83, 9, 0.12);
    color: #b45309;
    border-color: rgba(180, 83, 9, 0.3);
  }
  &.mp-card__seats--full {
    background: rgba(220, 38, 38, 0.12);
    color: #dc2626;
    border-color: rgba(220, 38, 38, 0.3);
  }
}
</style>
